/**
 * @fileoverview Weekly Calendar View Component
 * @description Custom calendar component for displaying doctor's weekly schedule
 * @author MCHD Room Scheduler Team
 * @created 07-10-2025
 * @complies @ui-ux-excellence.mdc - Custom component with Mantine primitives, WCAG 2.1 AA
 */

import { useState } from 'react';
import {
  Card,
  Text,
  Group,
  Stack,
  ActionIcon,
  Paper,
  Badge,
  ScrollArea,
  Grid,
  Button,
} from '@mantine/core';
import {
  IconChevronLeft,
  IconChevronRight,
  IconCalendarEvent,
  IconClock,
  IconMapPin,
} from '@tabler/icons-react';
import {
  useWeeklySchedule,
  extractWeeklySchedule,
  getScheduleError,
} from '../../../hooks/doctor/useDoctorSchedule';
import {
  getCurrentWeekStart,
  getNextWeekStart,
  getPreviousWeekStart,
  formatDateISO,
} from '../../../api/endpoints/doctor';
import type { DoctorAssignment } from '../../../types/doctor';

/**
 * Props for WeeklyCalendarView component
 */
export interface WeeklyCalendarViewProps {
  /** Optional initial week start date (defaults to current week) */
  initialWeekStart?: Date;
  
  /** Callback when assignment is clicked */
  onAssignmentClick?: (assignment: DoctorAssignment) => void;
  
  /** Optional custom class name */
  className?: string;
  
  /** Optional maximum height for scrolling */
  maxHeight?: number;
}

/**
 * Weekly Calendar View Component
 * @description Displays doctor's schedule in a 7-day week grid format
 * 
 * @example
 * ```tsx
 * function DoctorSchedulePage() {
 *   const [selectedAssignment, setSelectedAssignment] = useState(null);
 *   
 *   return (
 *     <div>
 *       <WeeklyCalendarView onAssignmentClick={setSelectedAssignment} />
 *       {selectedAssignment && (
 *         <AssignmentDetailsModal assignment={selectedAssignment} />
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function WeeklyCalendarView({
  initialWeekStart,
  onAssignmentClick,
  className,
  maxHeight = 600,
}: WeeklyCalendarViewProps) {
  // Week navigation state
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(
    initialWeekStart || getCurrentWeekStart()
  );

  // Fetch weekly schedule
  const scheduleQuery = useWeeklySchedule(currentWeekStart);
  const schedule = extractWeeklySchedule(scheduleQuery);
  const error = getScheduleError(scheduleQuery);

  /**
   * Navigate to previous week
   */
  const goToPreviousWeek = () => {
    setCurrentWeekStart(getPreviousWeekStart(currentWeekStart));
  };

  /**
   * Navigate to next week
   */
  const goToNextWeek = () => {
    setCurrentWeekStart(getNextWeekStart(currentWeekStart));
  };

  /**
   * Go to current week
   */
  const goToCurrentWeek = () => {
    setCurrentWeekStart(getCurrentWeekStart());
  };

  /**
   * Generate array of 7 dates for the current week (Sunday-Saturday)
   */
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(currentWeekStart);
    date.setDate(currentWeekStart.getDate() + i);
    return date;
  });

  /**
   * Check if current week is the current week
   */
  const isCurrentWeek = formatDateISO(currentWeekStart) === formatDateISO(getCurrentWeekStart());

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder {...(className && { className })}>
      <Stack gap="md">
        {/* Header with navigation */}
        <Group justify="space-between">
          <Group gap="md">
            <ActionIcon
              variant="light"
              size="lg"
              onClick={goToPreviousWeek}
              aria-label="Previous week"
            >
              <IconChevronLeft size={18} />
            </ActionIcon>

            <div>
              <Text fw={600} size="lg">
                {formatWeekRange(currentWeekStart)}
              </Text>
              <Text size="sm" c="dimmed">
                Week View
              </Text>
            </div>

            <ActionIcon
              variant="light"
              size="lg"
              onClick={goToNextWeek}
              aria-label="Next week"
            >
              <IconChevronRight size={18} />
            </ActionIcon>
          </Group>

          {!isCurrentWeek && (
            <Button
              variant="light"
              size="xs"
              onClick={goToCurrentWeek}
              leftSection={<IconCalendarEvent size={14} />}
            >
              Current Week
            </Button>
          )}
        </Group>

        {/* Calendar Grid */}
        {scheduleQuery.isLoading ? (
          <CalendarSkeleton />
        ) : error ? (
          <Paper p="xl" radius="md" withBorder bg="red.0">
            <Text c="red" ta="center">
              {error}
            </Text>
          </Paper>
        ) : (
          <ScrollArea h={maxHeight} type="auto">
            <Grid gutter="xs" role="table" aria-label="Weekly calendar">
              {weekDays.map((day) => {
                const dateKey = formatDateISO(day);
                const dayAssignments = schedule[dateKey] || [];
                const isToday = isDateToday(day);
                const isPast = day < new Date(new Date().setHours(0, 0, 0, 0));

                return (
                  <Grid.Col key={dateKey} span={{ base: 12, md: 6, lg: 1.714 }}>
                    <Paper
                      p="sm"
                      radius="md"
                      withBorder
                      bg={isToday ? 'blue.0' : isPast ? 'gray.0' : 'white'}
                      style={{ minHeight: 150 }}
                      role="cell"
                    >
                      {/* Day Header */}
                      <Group justify="space-between" mb="xs">
                        <div>
                          <Text size="xs" fw={500} tt="uppercase" c="dimmed">
                            {day.toLocaleDateString('en-US', { weekday: 'short' })}
                          </Text>
                          <Text size="lg" fw={700} c={isToday ? 'blue' : 'dark'}>
                            {day.getDate()}
                          </Text>
                        </div>
                        {isToday && (
                          <Badge size="xs" color="blue" variant="filled">
                            Today
                          </Badge>
                        )}
                      </Group>

                      {/* Assignments for this day */}
                      <Stack gap="xs">
                        {dayAssignments.length === 0 ? (
                          <Text size="xs" c="dimmed" ta="center" mt="md">
                            No sessions
                          </Text>
                        ) : (
                          dayAssignments.map((assignment) => (
                            <AssignmentCard
                              key={assignment.id}
                              assignment={assignment}
                              onClick={() => onAssignmentClick?.(assignment)}
                            />
                          ))
                        )}
                      </Stack>
                    </Paper>
                  </Grid.Col>
                );
              })}
            </Grid>
          </ScrollArea>
        )}
      </Stack>
    </Card>
  );
}

/**
 * Assignment card for calendar day
 * @param assignment - Assignment data
 * @param onClick - Click handler
 */
function AssignmentCard({
  assignment,
  onClick,
}: {
  assignment: DoctorAssignment;
  onClick?: () => void;
}) {
  return (
    <Paper
      p="xs"
      radius="sm"
      withBorder
      bg="blue.0"
      style={{ cursor: onClick ? 'pointer' : 'default' }}
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={`Assignment: ${assignment.sessionTime.name} at ${assignment.room.number}`}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && onClick) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <Stack gap={4}>
        {/* Session time */}
        <Group gap={4} wrap="nowrap">
          <IconClock size={12} stroke={1.5} style={{ flexShrink: 0 }} aria-hidden="true" />
          <Text size="xs" fw={600} lineClamp={1}>
            {assignment.sessionTime.name}
          </Text>
        </Group>

        {/* Time range */}
        <Text size="xs" c="dimmed">
          {assignment.sessionTime.startTime} - {assignment.sessionTime.endTime}
        </Text>

        {/* Room */}
        <Group gap={4} wrap="nowrap">
          <IconMapPin size={12} stroke={1.5} style={{ flexShrink: 0 }} aria-hidden="true" />
          <Text size="xs" lineClamp={1}>
            {assignment.room.number}
          </Text>
        </Group>

        {/* Status badge */}
        <Badge size="xs" color={getStatusColor(assignment.status)} variant="light">
          {assignment.status}
        </Badge>
      </Stack>
    </Paper>
  );
}

/**
 * Skeleton loader for calendar
 */
function CalendarSkeleton() {
  return (
    <Grid gutter="xs">
      {[...Array(7)].map((_, i) => (
        <Grid.Col key={i} span={{ base: 12, md: 6, lg: 1.714 }}>
          <Paper p="sm" radius="md" withBorder style={{ minHeight: 150 }}>
            <div style={{ height: 20, width: 60, backgroundColor: '#e9ecef', borderRadius: 4 }} />
            <div style={{ height: 32, width: 40, backgroundColor: '#e9ecef', borderRadius: 4, marginTop: 8 }} />
          </Paper>
        </Grid.Col>
      ))}
    </Grid>
  );
}

/**
 * Format week range for display
 * @param weekStart - Week start date (Sunday)
 * @returns Formatted string (e.g., "Oct 8 - Oct 14, 2025")
 */
function formatWeekRange(weekStart: Date): string {
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);

  const startMonth = weekStart.toLocaleDateString('en-US', { month: 'short' });
  const endMonth = weekEnd.toLocaleDateString('en-US', { month: 'short' });
  const year = weekStart.getFullYear();

  if (startMonth === endMonth) {
    return `${startMonth} ${weekStart.getDate()} - ${weekEnd.getDate()}, ${year}`;
  } else {
    return `${startMonth} ${weekStart.getDate()} - ${endMonth} ${weekEnd.getDate()}, ${year}`;
  }
}

/**
 * Get badge color for assignment status
 * @param status - Assignment status
 * @returns Mantine color name
 */
function getStatusColor(status: string): string {
  switch (status.toUpperCase()) {
    case 'CONFIRMED':
      return 'green';
    case 'PENDING':
      return 'yellow';
    case 'CANCELLED':
      return 'red';
    case 'COMPLETED':
      return 'gray';
    default:
      return 'blue';
  }
}

/**
 * Check if date is today
 * @param date - Date to check
 * @returns True if date is today
 */
function isDateToday(date: Date): boolean {
  const today = new Date();
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
}

